import { observer } from "mobx-react-lite";
import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Grid } from "semantic-ui-react";
import { useStore } from "../../app/stores/store";
import { Application, TicketWorkflowSteps } from "../../app/models/enums";
import { formatCurrency } from "../../app/common/util/functions";
import { NotificationMessage } from "../../app/common/extraComponents/NotificationMessages";
import { useNavigate } from "react-router-dom";

interface Props {
    workflowStep: TicketWorkflowSteps;
    ticketId: string | undefined;
    amountDue?: number;
    finalized?: boolean;
}

export default observer(function CustomerCreditLimitAlertMessage({
    workflowStep,
    ticketId,
    amountDue,
    finalized = false,
}: Props) {
    const navigate = useNavigate();
    
    const { t } = useTranslation(["common", "customer"]);

    const { 
        customerStore, 
        userStore,
    } = useStore();

    const { selectedCustomer, selectedAccountsReceivable } = customerStore;
    const { checkApplication } = userStore;
    const [isOpen, setIsOpen] = useState<boolean>(true);

    const limitMessage = useMemo(() => {
        if(selectedAccountsReceivable?.creditLeftToUse) {
            if(selectedAccountsReceivable?.creditLeftToUse! - (amountDue ?? 0.0) < 0) {
                if(!finalized) {
                    return `
                        ${t("limitAlert.balanceDueOf", { ns: "customer" })}
                        ${formatCurrency(selectedAccountsReceivable?.balanceDue!)}
                        ${t("limitAlert.isOverCreditLimitOf", { ns: "customer" })}
                        ${formatCurrency(selectedAccountsReceivable?.creditLimit!)}
                `;
                }
                return `
                        ${t("limitAlert.amountDueOnTicketMay", { ns: "customer" })}
                        ${t("limitAlert.plusCurrentBalanceDueOf", { ns: "customer" })}
                        ${formatCurrency(selectedAccountsReceivable?.balanceDue!)}
                        ${t("limitAlert.withCreditLimitMay", { ns: "customer" })}
                        ${formatCurrency(selectedAccountsReceivable?.creditLimit!)}
                `;
            }

            // If the credit is not in negative, when credit left to use or available credit is less than 20%, throw up a limit message.
            let limitAlert = (selectedAccountsReceivable.creditLimit! * 0.20);    
            if(selectedAccountsReceivable?.creditLeftToUse! < limitAlert)
                return `
                        ${t("limitAlert.withCreditLimit", { ns: "customer" })}
                        ${formatCurrency(selectedAccountsReceivable?.creditLimit!)}
                        ${t("limitAlert.reachLimitOfBalanceDue", { ns: "customer" })}
                        ${formatCurrency(selectedAccountsReceivable?.balanceDue!)}
                `;
        }
        return '';
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedAccountsReceivable, selectedCustomer])

    return (
        <>
            {isOpen && checkApplication(Application.AutomotiveExpert) && selectedAccountsReceivable?.isActive && (
                <NotificationMessage hideCloseIcon={true} >
                    <Grid>
                        <Grid.Row>
                            <Grid.Column width={11} verticalAlign="middle" textAlign="left">
                                <h4 className="text-white">
                                    {limitMessage}
                                </h4>
                            </Grid.Column>
                            <Grid.Column textAlign="right" width={5} verticalAlign="middle">
                                <Button size="small" varient="link" onClick={() => navigate("/accountsReceivableIndex?id=" + selectedCustomer?.id)} type='button'>
                                    {t("limitAlert.payOffBalance", { ns: "customer" })}
                                </Button>
                                <Button size="small" varient="danger" onClick={() => setIsOpen(false)} variant="link" type='button'>
                                    {t("limitAlert.ignore", { ns: "customer" })}
                                </Button>
                            </Grid.Column>
                            {/* <Grid.Column>

                            </Grid.Column> */}
                        </Grid.Row>
                    </Grid>
                </NotificationMessage>
            )}
        </>
    );
});
